const FOSTER_GA4_MEASUREMENT_ID = 'G-8Z47MSGH29';
const FOSTER_CART_PROMPT_VIEW_EVENT = 'foster_cart_prompt_view';
const FOSTER_CART_PROMPT_CONVERSION_EVENT = 'foster_cart_prompt_conversion';
const fosterPromptViews = new Set();
const fosterPendingAnalyticsEvents = [];
let fosterPrivacyReadyPromise;
let fosterGtagConfigured = false;

function fosterCartAttributionKey(type, identifier) {
  return `_foster_cart_prompt_${type}_${identifier}`.slice(0, 255);
}

function fosterCartAttributionValue(parts) {
  return parts.filter(Boolean).join(' | ').replace(/\s+/g, ' ').slice(0, 255);
}

function fosterPrivacyReady() {
  if (fosterPrivacyReadyPromise) return fosterPrivacyReadyPromise;

  fosterPrivacyReadyPromise = new Promise((resolve) => {
    if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed) {
      resolve(true);
      return;
    }

    if (!window.Shopify?.loadFeatures) {
      resolve(false);
      return;
    }

    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => resolve(!error && Boolean(window.Shopify?.customerPrivacy))
    );
  });

  return fosterPrivacyReadyPromise;
}

function fosterAnalyticsAllowed() {
  return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
}

function fosterAnalyticsConsentPending() {
  return window.Shopify?.customerPrivacy?.currentVisitorConsent?.().analytics === '';
}

function fosterConfigureGtag() {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };

  if (fosterGtagConfigured) return;

  if (!document.getElementById('foster-ga4-custom-events')) {
    const script = document.createElement('script');
    script.id = 'foster-ga4-custom-events';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${FOSTER_GA4_MEASUREMENT_ID}`;
    document.head.appendChild(script);
  }

  window.gtag('js', new Date());
  window.gtag('config', FOSTER_GA4_MEASUREMENT_ID, { send_page_view: false });
  fosterGtagConfigured = true;
}

function fosterGa4Parameters(detail) {
  return Object.fromEntries(
    Object.entries(detail).filter(([key, value]) => key !== 'accepted_at' && value !== undefined && value !== null)
  );
}

function fosterSendAllowedAnalyticsEvent(eventName, detail) {
  if (!fosterAnalyticsAllowed()) return;

  if (window.Shopify?.analytics?.publish) {
    Promise.resolve(window.Shopify.analytics.publish(`foster:${eventName.replace(/^foster_/, '')}`, detail)).catch(() => {});
  }

  fosterConfigureGtag();
  window.gtag('event', eventName, {
    ...fosterGa4Parameters(detail),
    send_to: FOSTER_GA4_MEASUREMENT_ID
  });
}

function fosterQueueAnalyticsEvent(eventName, detail) {
  fosterPrivacyReady().then((ready) => {
    if (!ready) return;

    if (fosterAnalyticsAllowed()) {
      fosterSendAllowedAnalyticsEvent(eventName, detail);
    } else if (fosterAnalyticsConsentPending()) {
      fosterPendingAnalyticsEvents.push({ eventName, detail });
    }
  });
}

function fosterPublishCartPromptEvent(eventName, detail) {
  const eventDetail = {
    source: 'cart_prompt',
    ...detail
  };

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName, ...eventDetail });
  document.dispatchEvent(
    new CustomEvent(`foster:${eventName.replace(/^foster_/, '').replaceAll('_', '-')}`, { detail: eventDetail })
  );
  fosterQueueAnalyticsEvent(eventName, eventDetail);
}

document.addEventListener('visitorConsentCollected', () => {
  if (!fosterAnalyticsAllowed()) {
    fosterPendingAnalyticsEvents.length = 0;
    return;
  }

  fosterPendingAnalyticsEvents.splice(0).forEach(({ eventName, detail }) => {
    fosterSendAllowedAnalyticsEvent(eventName, detail);
  });
});

function fosterObservePromptView(element, detailFactory) {
  const publishView = () => {
    const detail = detailFactory();
    const viewKey = [detail.upgrade_type, detail.variant_id, detail.selling_plan_id].filter(Boolean).join(':');
    if (!viewKey || fosterPromptViews.has(viewKey)) return;

    fosterPromptViews.add(viewKey);
    fosterPublishCartPromptEvent(FOSTER_CART_PROMPT_VIEW_EVENT, detail);
  };

  if (!('IntersectionObserver' in window)) {
    publishView();
    return null;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) return;
      observer.disconnect();
      publishView();
    },
    { threshold: 0.5 }
  );
  observer.observe(element);
  return observer;
}

async function fosterSaveCartAttribution(attributes) {
  let lastError;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ attributes })
      });

      const cart = await response.json();
      if (!response.ok || cart.errors) {
        throw new Error(cart.description || cart.message || 'Cart attribution update failed');
      }

      return cart;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError;
}

if (!customElements.get('cart-size-upgrade')) {
  class CartSizeUpgrade extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-size-upgrade-button]');
      this.error = this.querySelector('[data-size-upgrade-error]');
      this.handleUpgrade = this.upgrade.bind(this);
      this.button?.addEventListener('click', this.handleUpgrade);
      this.promptViewObserver = fosterObservePromptView(this, () => ({
        upgrade_type: 'size_12oz_to_5lb',
        variant_id: this.dataset.variantId
      }));
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.handleUpgrade);
      this.promptViewObserver?.disconnect();
    }

    async upgrade() {
      if (!this.button || this.button.disabled) return;

      const lineKey = this.dataset.lineKey;
      const variantId = this.dataset.variantId;
      if (!lineKey || !variantId) return;

      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      this.button.textContent = this.button.dataset.loadingLabel;
      this.error.textContent = '';

      const acceptedAt = new Date().toISOString();
      const attributionKey = fosterCartAttributionKey('5lb', variantId);
      const attributionValue = fosterCartAttributionValue([
        `accepted_at=${acceptedAt}`,
        'upgrade=12oz_to_5lb',
        `source_line=${lineKey}`,
        `target_variant=${variantId}`
      ]);
      const updates = {
        [lineKey]: 0,
        [variantId]: 1
      };

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            updates,
            attributes: {
              [attributionKey]: attributionValue
            }
          })
        });

        const cart = await response.json();
        if (!response.ok || cart.errors) {
          throw new Error(cart.description || cart.message || 'Cart update failed');
        }

        fosterPublishCartPromptEvent(FOSTER_CART_PROMPT_CONVERSION_EVENT, {
          upgrade_type: 'size_12oz_to_5lb',
          variant_id: variantId,
          accepted_at: acceptedAt,
          order_attribution_saved: true
        });

        const cartItems = this.closest('cart-items');

        if (cartItems && typeof cartItems.getCart === 'function') {
          cartItems.getCart();
        } else {
          window.location.reload();
        }
      } catch (error) {
        this.error.textContent = this.dataset.errorLabel;
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
        this.button.textContent = this.button.dataset.defaultLabel;
      }
    }
  }

  customElements.define('cart-size-upgrade', CartSizeUpgrade);
}

if (!customElements.get('cart-subscription-upgrade')) {
  class CartSubscriptionUpgrade extends HTMLElement {
    connectedCallback() {
      this.controls = this.querySelector('[data-subscription-controls]');
      this.select = this.querySelector('[data-subscription-plan]');
      this.button = this.querySelector('[data-subscription-button]');
      this.titleElement = this.querySelector('[data-subscription-title]');
      this.priceElement = this.querySelector('[data-subscription-price]');
      this.error = this.querySelector('[data-subscription-error]');
      this.handleClick = this.subscribe.bind(this);
      this.handleChange = this.updateDetails.bind(this);
      this.button?.addEventListener('click', this.handleClick);
      this.select?.addEventListener('change', this.handleChange);
      this.promptViewObserver = fosterObservePromptView(this, () => ({
        upgrade_type: 'subscription',
        variant_id: this.dataset.variantId,
        selling_plan_id: this.select?.value,
        selling_plan_name: this.select?.selectedOptions[0]?.textContent?.trim()
      }));
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.handleClick);
      this.select?.removeEventListener('change', this.handleChange);
      this.promptViewObserver?.disconnect();
    }

    updateDetails() {
      const option = this.select?.selectedOptions[0];
      if (!option) return;

      this.titleElement.textContent = option.dataset.title;
      this.priceElement.textContent = option.dataset.priceLabel;
    }

    async subscribe(event) {
      event.preventDefault();
      if (!this.button || this.button.disabled || !this.select) return;

      const line = Number.parseInt(this.dataset.line, 10);
      const quantity = Number.parseInt(this.dataset.quantity, 10);
      const sellingPlan = Number.parseInt(this.select.value, 10);
      if (!line || !quantity || !sellingPlan) return;

      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      this.button.textContent = this.button.dataset.loadingLabel;
      this.error.textContent = '';

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            line,
            quantity,
            selling_plan: sellingPlan
          })
        });

        const cart = await response.json();
        if (!response.ok || cart.errors) {
          throw new Error(cart.description || cart.message || 'Subscription update failed');
        }

        const acceptedAt = new Date().toISOString();
        const changedItem = cart.items?.[line - 1];
        const variantId = changedItem?.variant_id || this.dataset.variantId || `line_${line}`;
        const planLabel = this.select.selectedOptions[0]?.textContent?.trim() || `selling_plan_${sellingPlan}`;
        const itemLabel = [changedItem?.product_title, changedItem?.variant_title].filter(Boolean).join(' - ');
        const attributionKey = fosterCartAttributionKey('subscription', `${variantId}_${sellingPlan}`);
        const attributionValue = fosterCartAttributionValue([
          `accepted_at=${acceptedAt}`,
          itemLabel,
          planLabel,
          `variant=${variantId}`,
          `selling_plan=${sellingPlan}`
        ]);
        let attributionSaved = false;

        try {
          await fosterSaveCartAttribution({
            [attributionKey]: attributionValue
          });
          attributionSaved = true;
        } catch (attributionError) {
          console.warn('Cart subscription attribution could not be saved.', attributionError);
        }

        fosterPublishCartPromptEvent(FOSTER_CART_PROMPT_CONVERSION_EVENT, {
          upgrade_type: 'subscription',
          variant_id: String(variantId),
          selling_plan_id: String(sellingPlan),
          selling_plan_name: planLabel,
          accepted_at: acceptedAt,
          order_attribution_saved: attributionSaved
        });

        const cartItems = this.closest('cart-items');
        if (cartItems && typeof cartItems.getCart === 'function') {
          cartItems.getCart();
        } else {
          window.location.reload();
        }
      } catch (error) {
        this.error.textContent = this.dataset.errorLabel;
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
        this.button.textContent = this.button.dataset.defaultLabel;
      }
    }
  }

  customElements.define('cart-subscription-upgrade', CartSubscriptionUpgrade);
}
